import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RealTimeTranscriptLine } from '../types';
import { formatTimestamp, formatUtcDateTime } from '../utils/formatters';

type SnapshotRow = {
  lineId: string;
  timeLabel: string;
  text: string;
};

type SearchMatch = {
  key: string;
  lineId: string;
  column: 'timeLabel' | 'text';
  start: number;
  end: number;
};

const DEBOUNCE_MS = 200;

function findAllOccurrences(haystack: string, needle: string): Array<[number, number]> {
  const results: Array<[number, number]> = [];
  if (!needle) return results;
  const lower = haystack.toLowerCase();
  let pos = 0;
  while (pos <= lower.length - needle.length) {
    const idx = lower.indexOf(needle, pos);
    if (idx === -1) break;
    results.push([idx, idx + needle.length]);
    pos = idx + 1;
  }
  return results;
}

function buildSnapshot(lines: RealTimeTranscriptLine[]): SnapshotRow[] {
  return lines
    .filter((line) => {
      if (line.isFinal !== true) return false;
      const hasText = typeof line.text === 'string' && line.text.length > 0;
      const hasIdentity =
        line.lineId != null || line.lineNumber != null || line.pageLineNumber != null;
      return hasText || hasIdentity;
    })
    .map((line) => ({
      lineId: line.id,
      timeLabel: line.finalizedAtUtc
        ? formatUtcDateTime(line.finalizedAtUtc)
        : formatTimestamp(line.timestamp),
      text: line.text ?? '',
    }));
}

function computeMatches(snapshot: SnapshotRow[], needle: string): SearchMatch[] {
  const results: SearchMatch[] = [];
  const lowerNeedle = needle.toLowerCase();

  for (const row of snapshot) {
    // timeLabel matches first (deterministic: timeLabel before text)
    for (const [start, end] of findAllOccurrences(row.timeLabel, lowerNeedle)) {
      results.push({
        key: `${row.lineId}:timeLabel:${start}:${end}`,
        lineId: row.lineId,
        column: 'timeLabel',
        start,
        end,
      });
    }

    // text matches second
    for (const [start, end] of findAllOccurrences(row.text, lowerNeedle)) {
      results.push({
        key: `${row.lineId}:text:${start}:${end}`,
        lineId: row.lineId,
        column: 'text',
        start,
        end,
      });
    }
  }

  return results;
}

export default function useTranscriptSearch(
  displayLines: RealTimeTranscriptLine[],
  scrollRef: React.RefObject<HTMLDivElement | null>,
) {
  const [query, setQueryRaw] = useState('');
  const [snapshot, setSnapshot] = useState<SnapshotRow[] | null>(null);
  const [matches, setMatches] = useState<SearchMatch[]>([]);
  const [activeMatchIndex, setActiveMatchIndex] = useState(-1);
  const [snapshotFinalizedCount, setSnapshotFinalizedCount] = useState(0);

  const debounceTimerRef = useRef<number | null>(null);
  const snapshotRef = useRef<SnapshotRow[] | null>(null);
  const displayLinesRef = useRef(displayLines);
  displayLinesRef.current = displayLines;

  const currentFinalizedCount = useMemo(() => {
    return displayLines.filter(
      (line) => line.isFinal === true,
    ).length;
  }, [displayLines]);

  const currentFinalizedCountRef = useRef(currentFinalizedCount);
  currentFinalizedCountRef.current = currentFinalizedCount;

  const runSearch = useCallback((q: string, snap: SnapshotRow[], prevActiveKey: string | null) => {
    const trimmed = q.trim();
    if (!trimmed) {
      setMatches([]);
      setActiveMatchIndex(-1);
      return;
    }

    const results = computeMatches(snap, trimmed);
    setMatches(results);

    if (results.length === 0) {
      setActiveMatchIndex(-1);
      return;
    }

    // Try to preserve active match by key
    if (prevActiveKey) {
      const idx = results.findIndex((m) => m.key === prevActiveKey);
      if (idx >= 0) {
        setActiveMatchIndex(idx);
        return;
      }
    }

    setActiveMatchIndex(0);
  }, []);

  const setQuery = useCallback(
    (q: string) => {
      setQueryRaw(q);

      if (debounceTimerRef.current != null) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      if (!q.trim()) {
        setMatches([]);
        setActiveMatchIndex(-1);
        setSnapshot(null);
        snapshotRef.current = null;
        return;
      }

      debounceTimerRef.current = window.setTimeout(() => {
        debounceTimerRef.current = null;

        let currentSnapshot = snapshotRef.current;
        if (!currentSnapshot) {
          currentSnapshot = buildSnapshot(displayLinesRef.current);
          setSnapshot(currentSnapshot);
          snapshotRef.current = currentSnapshot;
          setSnapshotFinalizedCount(currentFinalizedCountRef.current);
        }

        runSearch(q, currentSnapshot, null);
      }, DEBOUNCE_MS);
    },
    [runSearch],
  );

  const goNext = useCallback(() => {
    if (matches.length === 0) return;
    setActiveMatchIndex((prev) => (prev + 1) % matches.length);
  }, [matches.length]);

  const goPrev = useCallback(() => {
    if (matches.length === 0) return;
    setActiveMatchIndex((prev) => (prev - 1 + matches.length) % matches.length);
  }, [matches.length]);

  const clearSearch = useCallback(() => {
    setQueryRaw('');
    setSnapshot(null);
    snapshotRef.current = null;
    setMatches([]);
    setActiveMatchIndex(-1);
    setSnapshotFinalizedCount(0);
    if (debounceTimerRef.current != null) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const refreshSnapshot = useCallback(() => {
    const newSnapshot = buildSnapshot(displayLinesRef.current);
    setSnapshot(newSnapshot);
    snapshotRef.current = newSnapshot;
    setSnapshotFinalizedCount(currentFinalizedCountRef.current);

    // Re-run search, try to preserve active match key
    const currentActiveKey =
      activeMatchIndex >= 0 && matches[activeMatchIndex]
        ? matches[activeMatchIndex].key
        : null;

    runSearch(query, newSnapshot, currentActiveKey);
  }, [activeMatchIndex, matches, query, runSearch]);

  // Scroll to active match
  useEffect(() => {
    if (activeMatchIndex < 0 || !matches[activeMatchIndex]) return;
    const container = scrollRef.current;
    if (!container) return;

    const lineId = matches[activeMatchIndex].lineId;
    const el = container.querySelector<HTMLElement>(`[data-line-id="${lineId}"]`);
    if (!el) return;

    // Check if the element is already fully visible within the scroll container.
    // If it is, skip scrolling entirely to avoid unnecessary movement.
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const isFullyVisible =
      elRect.top >= containerRect.top && elRect.bottom <= containerRect.bottom;

    if (isFullyVisible) return;

    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [activeMatchIndex, matches, scrollRef]);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current != null) {
        window.clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const isSearchActive = query.trim().length > 0;

  const totalMatches = matches.length;

  const matchedLineIds = useMemo(() => {
    const set = new Set<string>();
    for (const m of matches) set.add(m.lineId);
    return set;
  }, [matches]);

  const activeLineId = useMemo(() => {
    if (activeMatchIndex < 0 || !matches[activeMatchIndex]) return null;
    return matches[activeMatchIndex].lineId;
  }, [activeMatchIndex, matches]);

  const hasPendingNewLines = useMemo(() => {
    if (!snapshot) return false;
    return currentFinalizedCount > snapshotFinalizedCount;
  }, [snapshot, currentFinalizedCount, snapshotFinalizedCount]);

  return {
    query,
    isSearchActive,
    matches,
    activeMatchIndex,
    totalMatches,
    matchedLineIds,
    activeLineId,
    hasPendingNewLines,
    setQuery,
    goNext,
    goPrev,
    clearSearch,
    refreshSnapshot,
  };
}
